import React, { FC, Dispatch, SetStateAction, ReactNode } from 'react';
import { StarsRatingBarProps } from '@wix/design-system/dist/types/StarsRatingBar';
import { StatusIndicatorState } from '@wix/design-system/dist/types/StatusIndicator';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { Box, ModalMobileLayout, Text, Button } from '@wix/design-system';

import StarRating from '@components/Reviews/ReviewModal/StarRating';
import ReviewTextArea from '@components/Reviews/ReviewModal/ReviewTextArea';

export type MobileModalContentProps = {
  title: string;
  textAreaLabel: string;
  textAreaPlaceholder?: string;
  TemplateDetailsComponent?: any;
  ratingScore: StarsRatingBarProps['value'];
  setRatingScore: (score: StarsRatingBarProps['value']) => void;
  onClose: () => void;
  onCancel: () => void;
  submitReview: () => void;
  primaryButtonText: string | ReactNode;
  reviewScoreStatus: StatusIndicatorState | undefined;
  isReviewScoreChanged: boolean;
  setReviewScoreError: Dispatch<
    SetStateAction<StatusIndicatorState | undefined>
  >;
  setIsReviewScoreChanged: Dispatch<SetStateAction<boolean>>;
  reviewText: string;
  reviewTextStatus: StatusIndicatorState | undefined;
  isReviewTextChanged: boolean;
  setReviewText: Dispatch<SetStateAction<string>>;
  setReviewTextError: Dispatch<
    SetStateAction<StatusIndicatorState | undefined>
  >;
  setIsReviewTextChanged: Dispatch<SetStateAction<boolean>>;
  enableAutoFocus?: boolean;
  onRateChange?: (rate: number) => void;
};

const MobileModalContent: FC<MobileModalContentProps> = ({
  title,
  textAreaLabel,
  textAreaPlaceholder,
  TemplateDetailsComponent,
  ratingScore,
  setRatingScore,
  onClose,
  onCancel,
  submitReview,
  primaryButtonText,
  reviewScoreStatus,
  isReviewScoreChanged,
  setReviewScoreError,
  setIsReviewScoreChanged,
  reviewText,
  reviewTextStatus,
  isReviewTextChanged,
  setReviewText,
  setReviewTextError,
  setIsReviewTextChanged,
  enableAutoFocus,
  onRateChange,
}) => {
  return (
    <ModalMobileLayout
      dataHook="mobile-review-modal-layout"
      fullscreen
      stickyFooter
      title={<Text weight="bold">{title}</Text>}
      onCloseButtonClick={onClose}
      footer={
        <MobileFooter
          onCancel={onCancel}
          submitReview={submitReview}
          primaryButtonText={primaryButtonText}
        />
      }
      content={
        <Box direction="vertical" height="calc(100vh - 150px)">
          <Box direction="vertical" gap="30px">
            {TemplateDetailsComponent && TemplateDetailsComponent}
            <StarRating
              overallScore={ratingScore}
              reviewScoreStatus={reviewScoreStatus}
              isReviewScoreChanged={isReviewScoreChanged}
              setReviewScore={setRatingScore}
              setReviewScoreError={setReviewScoreError}
              setIsReviewScoreChanged={setIsReviewScoreChanged}
              onRateChange={onRateChange}
            />
            <ReviewTextArea
              textLabel={textAreaLabel}
              textPlaceholder={textAreaPlaceholder}
              reviewText={reviewText}
              reviewTextStatus={reviewTextStatus}
              isReviewTextChanged={isReviewTextChanged}
              setReviewText={setReviewText}
              setReviewTextError={setReviewTextError}
              setIsReviewTextChanged={setIsReviewTextChanged}
              enableAutoFocus={enableAutoFocus}
            />
          </Box>
        </Box>
      }
    />
  );
};

const MobileFooter: FC<{
  onCancel: () => void;
  submitReview: () => void;
  primaryButtonText: string | ReactNode;
}> = ({ onCancel, submitReview, primaryButtonText }) => {
  const { t } = useTranslation();
  return (
    <Box align="space-between" verticalAlign="middle">
      <Button
        priority="secondary"
        onClick={onCancel}
        dataHook="mobile-review-modal-cancel"
      >
        {t('client-dashboard-review-modal-cancel-button')}
      </Button>
      <Button dataHook="mobile-review-modal-submit" onClick={submitReview}>
        {primaryButtonText}
      </Button>
    </Box>
  );
};

export default MobileModalContent;
